import React, {useContext, useEffect, useMemo, useState} from "react";
import {Box, ResponsiveContext, Text} from "grommet";
import styled from "styled-components";
import useSWR from "swr";
import DataTableComponent from "../../components/DataTable";
import DateInput from "../../components/DateInput";
import {NotificationsContext} from "../../components/NotificationsProvider";
import {downloadBlobAsCsvFile} from "../../lib/exportcsv";
import {formatDate, formatddMMyyDate, getTodaysDate, toLocalDateString, parseLocalDate} from "../../utils/dates";
import {handleErrors, handleFileDownloadErrors} from "../../utils/errors";
import {formatCurrency} from "../../utils/numbers";
import PayoutsPage from "./index";
import {formatName} from "../../utils/names";
import {AGING_BALANCE_COLOR_MAP} from "../../constants/Colors";
import CustomerTag from "../../components/CustomerTag";
import GroupTag from "../../components/GroupTag";
import FontAwesomeIcon from "../../components/FontAwesomeIcon";
import { faChevronRight as IconChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { useRouter } from "next/router";
import Button from "../../components/Button";
import DropButton from "../../components/DropButton";

const TextAlignRight = styled(Box)`
    text-align: right;
    white-space: nowrap;
`

const sumField = (rows, field) => rows.reduce((sum, row) => sum + (parseFloat(row[field]) || 0), 0);

// The API sends credits as a positive magnitude. The report reads them as a deduction,
// so we flip the sign once here and let display, sorting and group totals share it.
const negateCredits = (row) => ({ ...row, credits: -(parseFloat(row.credits) || 0) });

const groupByCustomerGroup = (data, expandedGroups) => {
    if (!data || !Array.isArray(data)) return data;

    const groups = {};
    const ungrouped = [];

    data.map(negateCredits).forEach(row => {
        if (row.customer_group_uuid) {
            if (!groups[row.customer_group_uuid]) {
                groups[row.customer_group_uuid] = {
                    _isGroup: true,
                    _id: `group_${row.customer_group_uuid}`,
                    customer_group_uuid: row.customer_group_uuid,
                    company_name: row.customer_group_name,
                    _children: [],
                };
            }
            groups[row.customer_group_uuid]._children.push(row);
        } else {
            ungrouped.push({ ...row, _id: `customer_${row.id || ungrouped.length}` });
        }
    });

    const result = [];
    Object.values(groups).forEach(group => {
        group.not_due = sumField(group._children, 'not_due');
        group.between_1_and_30_days = sumField(group._children, 'between_1_and_30_days');
        group.between_31_and_60_days = sumField(group._children, 'between_31_and_60_days');
        group.between_61_and_90_days = sumField(group._children, 'between_61_and_90_days');
        group.over_91_days = sumField(group._children, 'over_91_days');
        group.credits = sumField(group._children, 'credits');
        group.total = sumField(group._children, 'total');
        group.currency = group._children[0]?.currency;
        result.push(group);
        if (expandedGroups.has(group.customer_group_uuid)) {
            group._children.forEach(child => {
                result.push({ ...child, _isChild: true, _id: `child_${child.id || result.length}` });
            });
        }
    });

    return [...result, ...ungrouped];
};

const AgingBalancePage = (props) => {
    const router = useRouter();
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        if (!router.isReady) return;
        if (router.query.report_date) setSelectedDate(parseLocalDate(router.query.report_date));
        setInitialized(true);
    }, [router.isReady]);

    useEffect(() => {
        if (!initialized) return;
        const query = {};
        if (selectedDate) query.report_date = toLocalDateString(selectedDate);
        router.replace({ pathname: router.pathname, query }, undefined, { shallow: true });
    }, [selectedDate, initialized]);

    const fetcher = (url) => fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    }).then(handleErrors)
        .catch(props.showNotification);

    const {data, error } = useSWR(
        `/api/reports/aging-balance?report_date=${toLocalDateString(selectedDate)}`,
        fetcher,
        { revalidateOnFocus: false }
    );

    return (
        <PayoutsPage title="Aging balance" {...props} hidePayout={true}>
            {data && (<AgingBalanceTable {...data} onDateChange={date => setSelectedDate(date)} date={selectedDate} />)}
        </PayoutsPage>
    );
};

const AgingBalanceColumnName = ({color, name}) => (
    <Box direction="row" align="center" gap="xsmall">
        {/* The colored dots next to column name (e.g. 1-30 days) */}
        <Box round="100%" background={color} width={{min: '10px'}} height={{min: '10px'}} />
        <TextAlignRight>{name}</TextAlignRight>
    </Box>
);

const LineInDrop = ({color, label, minorAmount}) => (
    <Box direction="row" align="center" gap="medium" justify="between">
        <Box direction="row" align="center" gap="xsmall">
            {color && (<Box round="100%" background={color} width={{min: '10px'}} height={{min: '10px'}} />)}
            <Text>{label}</Text>
        </Box>
        <Box>
            {formatCurrency(minorAmount)}
        </Box>
    </Box>
);

const AgingBalanceTable = ({data, currency, generated_on, report_date, onDateChange, date}) => {
    const [sortDirection, setSortDirection] = useState('asc');
    const notificationCtx = useContext(NotificationsContext);
    const router = useRouter();
    const size = useContext(ResponsiveContext);
    const isMobile = size === "small";
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [sortField, setSortField] = useState(null);

    const toggleGroup = (groupUuid) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            next.has(groupUuid) ? next.delete(groupUuid) : next.add(groupUuid);
            return next;
        });
    };

    const groupedData = useMemo(() => {
        const rows = groupByCustomerGroup(data, expandedGroups);
        if (!sortField || !rows) return rows;
        const topLevel = rows.filter(r => !r._isChild);
        const compareFn = (a, b) => {
            if (sortField === 'company_name') {
                return (a.company_name || '').localeCompare(b.company_name || '');
            }
            // null/undefined values at bottom
            if (typeof a[sortField] === 'undefined') return 1;
            if (typeof b[sortField] === 'undefined') return -1;
            return (parseFloat(a[sortField]) || 0) - (parseFloat(b[sortField]) || 0);
        };
        topLevel.sort((a, b) => sortDirection === 'asc' ? compareFn(a, b) : compareFn(b, a));
        const result = [];
        topLevel.forEach(row => {
            result.push(row);
            if (row._isGroup && expandedGroups.has(row.customer_group_uuid)) {
                rows.filter(r => r._isChild && r.customer_group_uuid === row.customer_group_uuid)
                    .forEach(child => result.push(child));
            }
        });
        return result;
    }, [data, expandedGroups, sortField, sortDirection]);

    const columns = isMobile ? [
        {
            selector: row => row._id,
            cell: (datum) => {
                return (
                    <DropButton
                        event={{section: 'aging_balance_table', element: 'row', description: datum._id}}
                        fill="horizontal"
                        focusIndicator={false}
                        plain
                        label={(
                            <Box fill="horizontal" justify="between" direction="row" gap="small" align="center" pad={{right: 'xsmall'}}>
                                <Box>
                                    <Text>{datum?.company_name || formatName(datum, true)}</Text>
                                </Box>
                                <Box>
                                    <Text wordBreak="keep-all">{formatCurrency(datum?.total, datum?.currency || currency)}</Text>
                                </Box>
                            </Box>
                        )}
                        dropContent={(
                            <Box pad="medium" border width={{max: 'medium'}} gap="medium" direction="column">
                                <Box pad={{vertical: 'xsmall'}} border={{side: 'bottom'}}>
                                    <LineInDrop label="Total" minorAmount={datum?.total} />
                                </Box>
                                <LineInDrop label="Not yet due" color={AGING_BALANCE_COLOR_MAP['<0']} minorAmount={datum?.not_due} />
                                <LineInDrop label="1-30 days" color={AGING_BALANCE_COLOR_MAP['0+']} minorAmount={datum?.between_1_and_30_days} />
                                <LineInDrop label="31-60 days" color={AGING_BALANCE_COLOR_MAP['30+']} minorAmount={datum?.between_31_and_60_days} />
                                <LineInDrop label="61-90 days" color={AGING_BALANCE_COLOR_MAP['60+']} minorAmount={datum?.between_61_and_90_days} />
                                <LineInDrop label="90+ days" color={AGING_BALANCE_COLOR_MAP['90+']} minorAmount={datum?.over_91_days} />
                                {datum?.credits ? (
                                    <Box pad={{top: 'xsmall'}} border={{side: 'top'}}>
                                        <LineInDrop label="Credits" minorAmount={datum?.credits} />
                                    </Box>
                                ) : null}
                                {!datum._isGroup && (
                                    <Button
                                        event={{section: 'aging_balance_table', element: 'view_profile'}}
                                        primary
                                        ctaColor="accent"
                                        color="accent"
                                        fill="horizontal"
                                        label={(
                                            <Box direction="row" gap="small" align="center" fill="horizontal" justify="center">
                                                <Text color="text">View profile</Text>
                                            </Box>
                                        )}
                                        onClick={() => router.push(`/customers/${datum?.id}`)}
                                    />
                                )}
                            </Box>
                        )}
                        dropAlign={{top: 'bottom', left: 'left'}}
                    />
                )
            }
        }
    ]: [
        {
            selector: row => row.company_name,
            name: 'Customer',
            sortable: true,
            sortField: 'company_name',
            grow: 2,
            cell: (datum) => datum._isGroup
                ? <Box direction="row" align="center" gap="xsmall">
                    <GroupTag id={datum.customer_group_uuid} inline />
                    <FontAwesomeIcon SelectedIcon={IconChevronRight} size="xxsmall" color="text-weak" styleProps={{ transform: expandedGroups.has(datum.customer_group_uuid) ? 'rotate(90deg)' : 'rotate(0deg)' }} />
                </Box>
                : datum._isChild
                    ? <Box pad={{ left: 'medium' }}><CustomerTag id={datum.id} inline /></Box>
                    : <CustomerTag id={datum.id} inline />
        },
        {
            selector: row => row.not_due,
            name: (<AgingBalanceColumnName color={AGING_BALANCE_COLOR_MAP['<0']} name="Not due" />),
            sortable: true,
            sortField: 'not_due',
            type: 'currency',
            right: true,
            cell: (datum) => datum.not_due ? formatCurrency(datum.not_due, datum.currency || currency) : null,
        },
        {
            selector: row => row.between_1_and_30_days,
            name: (<AgingBalanceColumnName color={AGING_BALANCE_COLOR_MAP['0+']} name="1-30 Days" />),
            sortable: true,
            sortField: 'between_1_and_30_days',
            type: 'currency',
            right: true,
            cell: (datum) => datum.between_1_and_30_days ? formatCurrency(datum.between_1_and_30_days, datum.currency || currency) : null,
        },
        {
            selector: row => row.between_31_and_60_days,
            name: (<AgingBalanceColumnName color={AGING_BALANCE_COLOR_MAP['30+']} name="31-60 Days" />),
            sortable: true,
            sortField: 'between_31_and_60_days',
            type: 'currency',
            right: true,
            cell: (datum) => datum.between_31_and_60_days ? formatCurrency(datum.between_31_and_60_days, datum.currency || currency) : null,
        },
        {
            selector: row => row.between_61_and_90_days,
            name: (<AgingBalanceColumnName color={AGING_BALANCE_COLOR_MAP['60+']} name="61-90 Days" />),
            sortable: true,
            sortField: 'between_61_and_90_days',
            type: 'currency',
            right: true,
            cell: (datum) => datum.between_61_and_90_days ? formatCurrency(datum.between_61_and_90_days, datum.currency || currency) : null,
        },
        {
            selector: row => row.over_91_days,
            name: (<AgingBalanceColumnName color={AGING_BALANCE_COLOR_MAP['90+']} name="91+ Days" />),
            sortable: true,
            sortField: 'over_91_days',
            type: 'currency',
            right: true,
            cell: (datum) => datum.over_91_days ? formatCurrency(datum.over_91_days, datum.currency || currency) : null,
        },
        {
            selector: row => row.credits,
            name: 'Credits',
            sortable: true,
            sortField: 'credits',
            type: 'currency',
            right: true,
            cell: (datum) => datum.credits ? formatCurrency(datum.credits, datum.currency || currency) : null,
        },
        {
            selector: row => row.total,
            name: 'Total',
            type: 'currency',
            right: true,
            sortable: true,
            sortField: 'total',
            cell: (datum) => formatCurrency(datum.total, datum.currency || currency)
        },
    ];
    const getAgingBalanceReportAsCsv = (callback) => {
        const url = `/api/reports/aging-balance/csv?report_date=${toLocalDateString(date || new Date())}`;
        const exportError = {type: 'error', message: 'Unable to download csv data. Try again later.'};
        fetch(url, {
            method: 'GET'
        })
            .then(handleFileDownloadErrors)
            .then((data) => {
                data ?
                    downloadBlobAsCsvFile(data, `aging_balance_as_of_${formatddMMyyDate(new Date(date))}_exported_on_${getTodaysDate()}.csv`) :
                    notificationCtx.showNotification(exportError);
                callback && callback();
            })
            .catch(e => notificationCtx.showNotification(exportError));
    };

    return (
        <Box>
            <DataTableComponent
                noTableHead={isMobile}
                data={groupedData}
                columns={columns}
                isServer={true}
                dataName="aging_balance_report"
                keyField="_id"
                onSort={(field, direction) => { setSortField(field); setSortDirection(direction); }}
                onRowClicked={(row) => row._isGroup && toggleGroup(row.customer_group_uuid)}
                conditionalRowStyles={[
                    { when: row => row._isChild, style: { backgroundColor: 'rgba(128, 128, 128, 0.08)' } },
                    { when: row => row._isGroup, style: { cursor: 'pointer' } },
                ]}
                rightControls={(
                    <DateInput value={date} onChange={onDateChange} labelPrefix="As of " defaultValue={new Date()} defaultLabel="today"/>
                )}
                dataTableWrapperProps={{pad: {horizontal: '0', vertical: 'xsmall'}}}
                controls={(
                    <Box direction="column" pad={{horizontal: 'small'}} align="start">
                        <Text size="large" weight={500}>{`Aging balance report as of ${formatDate(report_date)}`}</Text>
                    </Box>
                )}
                onExportCSVClicked={() => getAgingBalanceReportAsCsv()}
                showRowSeparators={true}/>
        </Box>
    );
}

export default AgingBalancePage;
